import { Row, RowItemType, Section, SectionType, SelectorIdentifier, UIElementType } from '../../models';
import { calculateMaintenanceInterval, convertDate, createSmartTranslationMessage } from '../../models/utils';
import { LocationCommunicationType } from '../../../location-card.constants';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { LocationStatus, RainLocation } from 'app/shared/models/locations';
import { LocationCardMonitorSeries, MonitorAddLocationSelectable } from 'app/shared/models/monitorsetting';
import {
    enableField,
    loadInstallationTypes,
    setGlobalConfig,
    pickMapCoords,
    openModemProviderDialog,
    statusChange,
    tagKeyChange,
    disableField,
    evaluateScanRateLock,
} from '../../actions';
import { CommonSectionIds } from '../common.models';
import { LocationCardStatus, RainGaugeLocation, SampleRateElement, ScanRateElement } from '../../../models/location-card.model';
import { BasicInfoCommonFieldIds } from '../common.models';
import { ValidatorIdentifier } from '../../models/validator-identifier';
import { field, FieldActions } from '../../core/field-schema';
import { required, maxLength, pattern, custom } from '../../core/validator-builders';
import { InstallationType } from 'app/shared/models/location-details';
import { InitialLocationCardState } from './model';
import { TextField } from '../../models/field-models';
import { DEFAULT_APN_PROVIDER } from 'app/shared/constant';
import { Selectable } from 'app/shared/models/selectable';

export const customPropertyRowGenerator = (index: number, populateValues?: { key: string; value: string }): Row => {
    return {
        info: {
            id: `custom-property-${index}`,
            panelClass: 'custom-50p',
        },
        items: [
            field.autocomplete<string, string>(`${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`, 'key', {
                optionsSelector: SelectorIdentifier.TagKeyAutocomplete,
                value: populateValues?.key,
                placeholder: 'ADD_EDIT.INPUT_TAG_NAME',
                validators: [required(), maxLength({ value: 128 })],
                panelClass: 'width-90',
                displayMapper: (option) => option,
                valueMapper: (option) => option,
                events: {
                    onBlur: (value) => [
                        tagKeyChange({
                            fieldId: `${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`,
                            newValue: value,
                            isMp1: true,
                        }),
                    ],
                },
            }),

            field.text(`${BasicInfoCommonFieldIds.CustomPropertyValue}-${index}`, 'value', {
                value: populateValues?.value,
                placeholder: 'ADD_EDIT.INPUT_TAG_VALUE',
                validators: [required(), maxLength({ value: 128 })],
                panelClass: 'width-90',
            }),
        ],
    };
};

/**
 * Triton form configuration
 */
export const basicInfo = (state: InitialLocationCardState): Section => {
    const settings = state.settings;
    const payload = state.locationDetails && state.locationDetails.length ? state.locationDetails[0] : null;
    const monitorConfig = state.monitorConfiguration;

    const rainGuage =
        payload && payload.assignedRainGaugeLocationId
            ? state.options.rainGauges!.data.find((v) => v.locationId === payload.assignedRainGaugeLocationId)
            : null;
    const isEditMode = !!payload;
    const showCoordsButton = state.locationCardData.enableSelectOnMap;

    const basicInfoConfig: Section = {
        info: {
            id: CommonSectionIds.BasicInfo,
            title: 'ADD_EDIT.ADD_EDIT_INFORMATION',
            displayTitle: true,
            expanded: true,
            expandable: true,
            panelClass: CommonSectionIds.BasicInfo,
        },
        rows: [
            // Row 1: Location Name and Series
            {
                info: {
                    id: 'basic-info-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.LocationName, 'locationName', {
                        value: isEditMode ? payload.locationName : null,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_NAME',
                        panelClass: 'custom-width',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            custom({
                                identifier: ValidatorIdentifier.UniqueLocationName,
                                message: 'COMMON.LOCATION_NAME_EXISTS',
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.trim().charAt(0).toUpperCase() + value.trim().slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    field.select<LocationCardMonitorSeries>(BasicInfoCommonFieldIds.Series, 'series', {
                        panelClass: 'custom-width',
                        value: isEditMode ? payload.series : null,
                        placeholder: 'COMMON.MONITOR_SERIES_PLACEHOLDER',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.MonitorSeriesOptions,
                        valueMapper: (option: LocationCardMonitorSeries) => option.value,
                        displayMapper: (option: LocationCardMonitorSeries) => option.text,
                        events: {
                            onChange: (value: LocationCardMonitorSeries) => [
                                loadInstallationTypes({ installationId: value.monitorSeriesID }),
                                setGlobalConfig({ monitorSeriesName: value.value }),
                            ],
                        },
                    }),
                ],
            },
            // Row 2: Serial Number and Status
            {
                info: {
                    id: 'basic-info-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.SerialNumber, 'serialnumber', {
                        panelClass: 'custom-width',
                        value: isEditMode ? payload.serialNumber : null,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SERIAL',
                        validators: [
                            required(),
                            maxLength({ value: 5 }),
                            pattern({
                                value: REGEX_CONFIG.numericOnlyPattern,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.SERIAL_NUMBER_ERROR',
                            }),
                        ],
                        events: {
                            onInput: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.replace(/[^0-9]/g, '');
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    {
                        id: BasicInfoCommonFieldIds.StatusSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'status-container',
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'status-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'status-row',
                                        panelClass: 'status-inner-row flex align-items-center gap-4',
                                    },
                                    items: [
                                        field.select<LocationCardStatus, number>(
                                            BasicInfoCommonFieldIds.Status,
                                            'status',
                                            {
                                                value: isEditMode ? payload.status : 0,
                                                panelClass: 'custom-width',
                                                disabled: !isEditMode,
                                                label: 'Status',
                                                valueMapper: (option) => option.key,
                                                displayMapper: (option) => option.name,
                                                validators: [required()],
                                                optionsSelector: SelectorIdentifier.LocationStatuses,
                                                events: {
                                                    onChange: () => [statusChange({ isMp1: true })],
                                                },
                                            },
                                        ),
                                        field.select<number, number>(
                                            BasicInfoCommonFieldIds.MaintenanceInterval,
                                            'maintenanceInterval',
                                            {
                                                value: isEditMode
                                                    ? calculateMaintenanceInterval(payload.maintenanceEnd)
                                                    : 2,
                                                label: 'CUSTOMER_EDITOR.HOURS',
                                                valueMapper: (option) => option,
                                                displayMapper: (option) => String(option),
                                                panelClass: 'width-15',
                                                validators: [required()],
                                                visible: isEditMode
                                                    ? payload.status === LocationStatus.Maintenance
                                                    : false,
                                                optionsSelector: SelectorIdentifier.MaintenanceIntervals,
                                            },
                                        ),
                                        {
                                            id: BasicInfoCommonFieldIds.MaintenanceEnd,
                                            name: 'maintenanceEnd',
                                            panelClass: 'width-25 field-status-info',
                                            label: isEditMode
                                                ? createSmartTranslationMessage('LOCATION_CARD_DYNAMIC_TRANSLATION.RETURN_TO_ACTIVE', {
                                                    date: convertDate(payload.maintenanceEnd, state.customer?.dateFormat, state.customer?.timeFormat)
                                                  })
                                                : 'CUSTOMER_EDITOR.HOURS',
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Text,
                                            visible: isEditMode ? payload.status === LocationStatus.Maintenance : false,
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },

            // Row 4: Communication and IP Address
            {
                info: {
                    id: 'basic-info-row-4',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<MonitorAddLocationSelectable, string>(
                        BasicInfoCommonFieldIds.CommunicationType,
                        'communicationType',
                        {
                            value: isEditMode
                                ? String(payload.communicationType)
                                : String(LocationCommunicationType.Wireless),
                            optionsSelector: SelectorIdentifier.CommunicationTypes,
                            valueMapper: (option) => option.value,
                            placeholder: 'COMMON.COMMUNICATION_TYPE_PLACEHOLDER',
                            displayMapper: (option) => option.text,
                            validators: [required()],
                            panelClass: 'custom-width',
                            events: {
                                onChange: (option: LocationCardMonitorSeries) => {
                                    const isWireless = Number(option.value) === LocationCommunicationType.Wireless;
                                    return [
                                        isWireless
                                            ? enableField({ fieldId: BasicInfoCommonFieldIds.IpAddress })
                                            : disableField({ fieldId: BasicInfoCommonFieldIds.IpAddress }),
                                    ];
                                },
                            },
                        },
                    ),
                    field.text(BasicInfoCommonFieldIds.IpAddress, 'ipaddress', {
                        value: isEditMode ? payload.connectionString : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_IP',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.ipaddressPattern,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.IP_ADDRESS_ERROR',
                            }),
                            maxLength({ value: 100 }),
                        ],
                    }),
                ],
            },

            // Row 5: APN Provider and Description
            {
                info: {
                    id: 'basic-info-row-5',
                    panelClass: 'custom-50p',
                },
                items: [
                    {
                        id: 'modem-provider-section',
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'modem-provider-container',
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'modem-provider-row',
                                        panelClass: 'custom-flat-button flex',
                                    },
                                    items: [
                                        field.select<Selectable, string>(
                                            BasicInfoCommonFieldIds.ApnProvider,
                                            'apnProvider',
                                            {
                                                value: DEFAULT_APN_PROVIDER,
                                                panelClass: 'full-width',
                                                placeholder: 'COMMON.MODEM_PROVIDER',
                                                optionsSelector: SelectorIdentifier.ModemProviders,
                                                valueMapper: (option) => option.name,
                                                displayMapper: (option) => option.name,
                                                validators: [required()],
                                            },
                                        ),
                                        {
                                            id: BasicInfoCommonFieldIds.ApnProviderBtn,
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Button,
                                            name: 'modemProviderBtn',
                                            iconName: 'edit',
                                            tooltip: 'COMMON.EDIT_TOOLTIP',
                                            panelClass: 'no-margin',
                                            events: {
                                                onClick: () => [openModemProviderDialog()],
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                    field.text(BasicInfoCommonFieldIds.Description, 'description', {
                        value: isEditMode ? payload.description : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LOCATION',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            maxLength({ value: 257 }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                ],
            },

            // Row 6: Rain Gauge and Manhole Depth
            {
                info: {
                    id: 'location-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.autocomplete<RainLocation, string>(
                        BasicInfoCommonFieldIds.AssignedRainGauge,
                        'assignedRainGauge',
                        {
                            value: rainGuage ? rainGuage.locationName : null,
                            autoClearOnFocus: true,
                            panelClass: 'custom-width',
                            label: 'Assigned Rain Gauge',
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ASSIGNED',
                            clearable: true,
                            optionsSelector: SelectorIdentifier.RainGaugeAutocomplete,
                            displayMapper: (option) => option.locationName,
                            valueMapper: (option) => option.locationName,
                            validators: [maxLength({ value: 50 })],
                        },
                    ),
                    field.number(BasicInfoCommonFieldIds.ManholeDepth, 'manholedepth', {
                        value: isEditMode ? payload.manholeDepth : null,
                        panelClass: 'custom-width',
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.MANHOLE_DEPTH',
                        placeholderSuffix: settings.depthUnit,
                        validators: [
                            pattern({
                                value: settings.isMetric
                                    ? REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern
                                    : REGEX_CONFIG.numeric0_100000With2DecimalPlacesPattern,
                                message: settings.isMetric
                                    ? 'COMMON.NUMERIC_PRECISION_ERROR_METRIC'
                                    : 'COMMON.NUMERIC_PRECISION_ERROR',
                            }),
                            maxLength({ value: 50 }),
                            custom({
                                identifier: ValidatorIdentifier.ManholeDepthToPipeHeightValidator,
                                message: 'COMMON.MAN_HOLE_DEPTH_PIPE_HEIGHT_ERROR',
                            }),
                        ],
                    }),
                ],
            },

            // Row 7: Address
            {
                info: {
                    id: 'location-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.ManholeAddress, 'manholeaddress', {
                        value: isEditMode ? payload.manholeAddress : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MANHOLE_ADDRESS',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            maxLength({
                                value: 64,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    {
                        id: BasicInfoCommonFieldIds.CoordinatesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CoordinatesSection,
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'coordinates-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'coordinates-row',
                                        panelClass: `coordinates-inner-row ${
                                            showCoordsButton ? 'flex-end' : 'justify-space-between'
                                        }`,
                                    },
                                    items: [
                                        field.number(BasicInfoCommonFieldIds.Latitude, 'latitude', {
                                            value: isEditMode ? payload.latitude : 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LATITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.latitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        field.number(BasicInfoCommonFieldIds.Longitude, 'longitude', {
                                            value: isEditMode ? payload.longitude : 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LONGITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.longitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        {
                                            id: 'move-location-icon',
                                            name: 'moveLocationIcon',
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Image,
                                            visible: !!showCoordsButton,
                                            imgSrc: '/assets/images/icons/move_icon.png',
                                            imgHeight: 18,
                                            imgWidth: 18,
                                            panelClass: 'move-icon',
                                            events: {
                                                onClick: () => [pickMapCoords()],
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },
            // Row 9: Sample Rates
            {
                info: {
                    id: 'settings-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<SampleRateElement, number>(BasicInfoCommonFieldIds.SampleRate, 'samplerate', {
                        value: null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SAMPLE_RATE',
                        validators: [required(), custom({ identifier: ValidatorIdentifier.NormalRateValidator })],
                        optionsSelector: SelectorIdentifier.SampleRates,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        events: {
                            onChange: () => [evaluateScanRateLock()],
                        },
                    }),
                    field.select<SampleRateElement, number>(BasicInfoCommonFieldIds.FastRate, 'fastrate', {
                        value: null,
                        panelClass: 'custom-width',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_FAST_SAMPLE_RATE',
                        validators: [
                            required(),
                            custom({
                                identifier: ValidatorIdentifier.FastRateValidator,
                                message: 'COMMON.TRITON_SAMPLE_RATE_ERROR',
                            }),
                        ],
                        optionsSelector: SelectorIdentifier.SampleRates,
                        events: {
                            onChange: () => [evaluateScanRateLock()],
                        },
                    }),
                ],
            },

            // Row 9a: Scan Rate
            {
                info: {
                    id: 'settings-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<ScanRateElement, number>(BasicInfoCommonFieldIds.ScanRate, 'scanRate', {
                        value: 0,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SCAN_RATE',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.ScanRates,
                        valueMapper: (option) => option.id,
                        displayMapper: (option) => option.text,
                    }),
                ],
            },

            // Row 10: Time Settings
            {
                info: {
                    id: 'settings-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(BasicInfoCommonFieldIds.SyncTime, 'synctime', {
                        value: true,
                        panelClass: 'custom-width flex item-flex-center',
                        label: 'ADD_EDIT.CLOCK_SYNCHRONIZATON',
                    }),

                    field.toggle(BasicInfoCommonFieldIds.UseDaylightSaving, 'usedaylightsaving', {
                        value: true,
                        panelClass: 'custom-width flex item-flex-center',
                        label: 'ADD_EDIT.DAYLIGHT_SAVING',
                    }),
                ],
            },
            // row 11: installation date
            {
                info: {
                    id: 'settings-row-3',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.date(BasicInfoCommonFieldIds.InstallationDate, 'installationDate', {
                        label: 'Installation Date',
                        placeholder: 'Select date',
                        dateFormat: settings.dateFormat,
                        datePickerType: 'date',
                        timeInterval: 1,
                        panelClass: 'full-width',
                        value: isEditMode ? payload.installationDate : new Date(),
                    }),
                ],
            },

            // Row 11: Installation Type and QFinal
            {
                info: {
                    id: 'installation-type-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<InstallationType, string>(
                        BasicInfoCommonFieldIds.InstallationType,
                        'installationType',
                        {
                            value: isEditMode ? `${payload.installationType}: ${payload.installationShapeTypeID}` : '',
                            disabled: true,
                            placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.INSTALLATION_TYPE',
                            validators: [required()],
                            optionsSelector: SelectorIdentifier.InstallationTypes,
                            panelClass: 'custom-width',
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                        },
                    ),
                ],
            },
            // Custom properties section
            {
                info: {
                    id: 'custom-properties-container-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                                title: 'ADD_EDIT.TAGS_SECTION_NAME',
                                displayTitle: false,
                                displayActionBtns: true,
                                type: SectionType.Array,
                                expanded: false,
                                expandable: false,
                                itemGenerator: customPropertyRowGenerator,
                                panelClass: 'custom-properties-panel',
                            },
                            rows: isEditMode
                                ? payload.customProperties.map((item, index) => customPropertyRowGenerator(index, item))
                                : [],
                        },
                    },
                ],
            },
        ],
    };

    return basicInfoConfig;
};
